import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Button,
  Checkbox,
  ClickAwayListener,
  Divider,
  Drawer,
  MenuItem,
  Menu,
  Table as MuiTable,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Tooltip,
  useMediaQuery,
} from "@mui/material";
import {
  Add as AddIcon,
  ExpandMore as ExpandMoreIcon,
  FilterList as FilterListIcon,
  SearchOutlined as SearchIcon,
  EditOutlined as EditOutlinedIcon,
  DeleteOutlined as DeleteOutlinedIcon,
  FolderSharedOutlined,
  KeyboardArrowDown as ArrowDownIcon,
} from "@mui/icons-material";
import { NumericFormat } from "react-number-format";
import { useAppDispatch, useAppSelector } from "../../../shared/hooks/sharedHooks";
import useTranslation from "../../../shared/hooks/useTranslation";
import { enumToArray, getName, NotificationMessage } from "../../../shared/models/models";
import {
  CustomerInventoryState,
  ProductAvailabilityCode,
  StockItem,
  StockItemsQuery,
  StockItemsQueryParameter,
  StockItemGroupStats,
  isStockItemsQueryEmpty,
  productAvailability,
} from "../models/models";
import { productCondition, ProductConditionCode, unitCode } from "../../../common/models/CommonModels";
import {
  changeQuery,
  deleteStockItem,
  deleteStockItems,
  getCategories,
  getManufacturers,
  getStockItemGroups,
  getStockItems,
  selectStockItem,
  setError,
  setSuccess,
} from "../customerInventorySlice";
import { useConfirmation } from "../../../shared/components/confirmation/ConfirmationProvider";
import ActionToast from "../../../shared/components/ActionToast";
import Loader from "../../../shared/components/Loader";
import SortTableHead, { TableColumnDefinition } from "../../../shared/components/SortTableHead";
import ImageBox from "../../../shared/components/ImageBox";
import TableActionButton from "../../../shared/components/tables/TableActionButton";
import LightTablePagination from "../../../shared/components/tables/LightTablePagination";
import TableEmpty from "../../../shared/components/tables/TableEmpty";
import BoxSearchIcon from "../../../shared/assets/icons/BoxSearchIcon";
import StockItemDetails from "./StockItemDetails";
import MoveStockItemDialog from "./MoveStockItemDialog";
import InventoryFilterBar from "./InventoryFilterBar";
import MobileStockItemCard from "./MobileStockItemCard";
import { dt, accentGradientDark, accentGradientDarkHover } from "../../../shared/themes/redesignTokens";
import PermissionGuard from "../../../shared/components/PermissionGuard";
import { Modules, PermissionLevel } from "../../../core/models/PermissionModels";

// ═══════════════════════════════════════════════════════════════
//  DESIGN TOKENS
// ═══════════════════════════════════════════════════════════════

// ═══════════════════════════════════════════════════════════════
//  GROUP & AVAILABILITY BADGE COLORS
// ═══════════════════════════════════════════════════════════════

const groupBadgeColors = {
  forSale: { bg: "#EAFAF1", text: "#27AE60", border: "#ABEBC6" },
  internal: { bg: "#F4F6F9", text: "#6B7280", border: "#D1D5DB" },
};

const availabilityBadgeColors: Record<number, { bg: string; text: string }> = {
  [ProductAvailabilityCode.inStock]: { bg: "#EAFAF1", text: "#27AE60" },
  [ProductAvailabilityCode.in7Days]: { bg: "#FEF9E7", text: "#F39C12" },
  [ProductAvailabilityCode.in14Days]: { bg: "#FEF9E7", text: "#E67E22" },
  [ProductAvailabilityCode.in1Month]: { bg: "#F9EBEA", text: "#C0392B" },
};

// ═══════════════════════════════════════════════════════════════
//  INLINE BADGES
// ═══════════════════════════════════════════════════════════════

const GroupBadge = ({ group, tInventory }: { group?: StockItemGroupStats; tInventory: (k: string) => string }) => {
  const isForSale = group ? !group.isPrivate : true;
  const gc = isForSale ? groupBadgeColors.forSale : groupBadgeColors.internal;
  return (
    <Box sx={{
      display: "inline-flex",
      alignItems: "center",
      backgroundColor: gc.bg,
      borderLeft: `3px solid ${gc.text}`,
      borderRadius: "0 8px 8px 0",
      padding: "4px 12px 4px 8px",
      fontSize: "12px",
      fontWeight: 600,
      fontFamily: dt.font,
      color: gc.text,
      whiteSpace: "nowrap",
    }}>
      {isForSale ? tInventory("forSale") : tInventory("internalStock")}
    </Box>
  );
};

const AvailabilityBadge = ({ code, tInventory }: { code: ProductAvailabilityCode; tInventory: (k: string, opts?: any) => string }) => {
  const ac = availabilityBadgeColors[code] || availabilityBadgeColors[ProductAvailabilityCode.inStock];
  return (
    <Box sx={{
      display: "inline-flex",
      alignItems: "center",
      backgroundColor: ac.bg,
      borderLeft: `3px solid ${ac.text}`,
      borderRadius: "0 8px 8px 0",
      padding: "4px 12px 4px 8px",
      fontSize: "12px",
      fontWeight: 600,
      fontFamily: dt.font,
      color: ac.text,
      whiteSpace: "nowrap",
    }}>
      {productAvailability(code, tInventory)}
    </Box>
  );
};

// ═══════════════════════════════════════════════════════════════
//  PAGINATION WRAPPER
// ═══════════════════════════════════════════════════════════════

const paginationWrapperSx = {
  "& *": { fontFamily: `${dt.font} !important` },
  "& span, & p, & div": { fontSize: "13px !important", letterSpacing: "0.1px" },
  "& .MuiIconButton-root": {
    color: dt.textSecondary,
    borderRadius: dt.radiusSm,
    transition: "all 0.2s",
    "&:hover": { backgroundColor: dt.blueGlow, color: dt.blue },
  },
};

// ═══════════════════════════════════════════════════════════════
//  TABLE STYLING
// ═══════════════════════════════════════════════════════════════

const tableSx = {
  width: "100%",
  "& thead th": {
    backgroundColor: dt.headerBg,
    borderBottom: `1px solid ${dt.cardBorder}`,
    fontSize: "11px !important",
    fontWeight: "700 !important",
    textTransform: "uppercase",
    letterSpacing: "1.2px",
    color: "#64748B !important",
    fontFamily: `${dt.font} !important`,
    padding: "14px 16px",
    whiteSpace: "nowrap",
  },
  // Blue header for Product column (column 2 because column 1 is checkbox)
  "& thead th:nth-of-type(2)": { color: `${dt.blue} !important` },
  "& thead th:nth-of-type(2) .MuiTableSortLabel-root": { color: `${dt.blue} !important` },
  "& tbody tr": {
    transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
    "&:hover": {
      backgroundColor: dt.rowHover,
      boxShadow: `inset 3px 0 0 0 ${dt.blue}`,
    },
  },
  "& tbody td": {
    borderBottom: `1px solid ${dt.rowBorder}`,
    padding: "12px 16px",
    fontSize: "14px",
    fontFamily: dt.font,
    color: dt.textPrimary,
  },
};

// ═══════════════════════════════════════════════════════════════
//  MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════

const InventoryTable = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const confirm = useConfirmation();
  const { translate: tInventory } = useTranslation("inventory");
  const { translate: tCommon } = useTranslation("common");

  const { query, stockItems, groups, stockItem, manufacturers, categories, loading, success, error } =
    useAppSelector<CustomerInventoryState>((state: any) => state.customerInventory);

  // 3-tier responsive
  const isCompact = useMediaQuery("(max-width:1200px)");
  const isPhone = useMediaQuery("(max-width:768px)");

  const [productDetails, setProductDetails] = useState(false);
  const [isMoveDialogOpen, setIsMoveDialogOpen] = useState(false);
  const [selectedItems, setSelectedItems] = useState<StockItem[]>([]);
  const [itemsToMove, setItemsToMove] = useState<StockItem[]>([]);
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const [manageAnchor, setManageAnchor] = useState<null | HTMLElement>(null);

  // Derived
  const forSaleGroup = groups.find((g) => !g.isPrivate);
  const internalGroup = groups.find((g) => g.isPrivate);
  const isQueryEmpty = isStockItemsQueryEmpty(query);

  // ─── Inject keyframes ──────────────────────────────────────

  useEffect(() => {
    const styleId = "inventory-redesign-keyframes";
    if (!document.getElementById(styleId)) {
      const style = document.createElement("style");
      style.id = styleId;
      style.textContent = `
        @keyframes fadeUp {
          0% { opacity: 0; transform: translateY(14px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        @keyframes headerReveal {
          0% { opacity: 0; transform: translateY(-8px); }
          100% { opacity: 1; transform: translateY(0); }
        }
      `;
      document.head.appendChild(style);
    }
  }, []);

  // ─── Init ──────────────────────────────────────────────────

  useEffect(() => {
    const initQuery = { ...query, groupId: undefined };
    dispatch(changeQuery(initQuery));
    dispatch(getStockItems(initQuery));
    dispatch(getManufacturers());
    dispatch(getCategories());
    dispatch(getStockItemGroups());
  }, []);

  // ─── Column definitions ────────────────────────────────────

  const columns: TableColumnDefinition[] = [
    { title: tInventory("product"), parameter: StockItemsQueryParameter.number },
    { title: tInventory("brand") },
    { title: tInventory("group") },
    { title: tInventory("condition") },
    { title: tInventory("availability") },
    { title: tInventory("price"), parameter: StockItemsQueryParameter.price },
    { title: tInventory("qty"), parameter: StockItemsQueryParameter.quantity },
    { title: undefined },
  ];

  // ─── Event handlers ────────────────────────────────────────

  const handleQueryChange = useCallback((newQuery: StockItemsQuery) => {
    setSelectedItems([]);
    dispatch(changeQuery(newQuery));
    dispatch(getStockItems(newQuery));
  }, [dispatch]);

  const handleSort = (parameter: number) => {
    const sortDesc = query.sortBy !== parameter ? false : !query.sortDesc;
    const newQuery = { ...query, sortBy: parameter, sortDesc };
    setSelectedItems([]);
    dispatch(changeQuery(newQuery));
    dispatch(getStockItems(newQuery));
  };

  const handlePageSizeChange = (size: number) => {
    const newQuery = { ...query, pageNo: 1, pageSize: size };
    setSelectedItems([]);
    dispatch(changeQuery(newQuery));
    dispatch(getStockItems(newQuery));
  };

  const handlePageNoChange = (no: number) => {
    const newQuery = { ...query, pageNo: no };
    setSelectedItems([]);
    dispatch(changeQuery(newQuery));
    dispatch(getStockItems(newQuery));
  };

  const handleSelect = (item: StockItem) => {
    setProductDetails(false);
    const exists = selectedItems.find((x) => x.id === item.id);
    if (exists) {
      setSelectedItems(selectedItems.filter((x) => x.id !== item.id));
    } else {
      setSelectedItems([...selectedItems, item]);
    }
  };

  const handleChangeSelection = () => {
    if (selectedItems.length === stockItems?.items.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems(stockItems?.items || []);
    }
  };

  const handleStockItemSelect = (item: StockItem) => {
    dispatch(selectStockItem(item));
    setProductDetails(true);
  };

  const handleClickAway = () => {
    dispatch(selectStockItem(null));
  };

  const handleFindInMarketplace = (item: StockItem) => {
    if (item) navigate(`/search?productNumber=${item.product.number}`);
  };

  const handleEdit = (item: StockItem) => {
    if (item) navigate(`/supplier/${item.id}`);
  };

  const handleDelete = async (item: StockItem) => {
    if (item) {
      if (selectedItems.find((x) => x.id === item.id)) {
        setSelectedItems(selectedItems.filter((x) => x.id !== item.id));
      }
      await dispatch(deleteStockItem(item.id));
      await dispatch(getStockItems(query));
    }
    setProductDetails(false);
  };

  const handleDeleteBulk = async () => {
    if (selectedItems.length > 0) {
      await dispatch(deleteStockItems(selectedItems.map((x) => x.id)));
      setSelectedItems([]);
      await dispatch(getStockItems(query));
    }
    setProductDetails(false);
  };

  const handleMoveItem = (items: StockItem[]) => {
    dispatch(selectStockItem(null));
    setItemsToMove(items);
    setIsMoveDialogOpen(true);
  };

  const handleChangeStockItemsGroup = async (value?: string) => {
    setIsMoveDialogOpen(false);
    if (value && selectedItems.find((x) => x.id === value)) {
      setSelectedItems(selectedItems.filter((x) => x.id !== value));
    }
    dispatch(getStockItems(query));
  };

  // ─── Manage Products menu ──────────────────────────────────

  const handleManageOpen = (e: React.MouseEvent<HTMLElement>) => {
    setManageAnchor(e.currentTarget);
  };

  const handleManageClose = () => setManageAnchor(null);

  // ─── Render table ──────────────────────────────────────────

  const renderTable = () => (
    <Box sx={{
      backgroundColor: dt.cardBg,
      borderRadius: dt.radius,
      border: `1px solid ${dt.cardBorder}`,
      boxShadow: dt.cardShadow,
      overflow: "hidden",
    }}>
      <TableContainer>
        <MuiTable sx={tableSx}>
        <SortTableHead
          columns={columns}
          sortBy={query.sortBy}
          sortDesc={query.sortDesc}
          onSort={handleSort}
          checkboxSelection={true}
          isChecked={selectedItems.length === (stockItems?.items.length || 0) && (stockItems?.items.length || 0) > 0}
          onChangeCheckbox={handleChangeSelection}
        />
        <TableBody>
          {stockItems?.items.map((item) => {
            const group = groups.find((g) => g.id === item.groupId);
            const isSelected = !!selectedItems.find((x) => x.id === item.id);
            const isViewing = item.id === stockItem?.id;

            return (
              <TableRow
                key={item.id}
                onClick={(e) => { e.stopPropagation(); handleStockItemSelect(item); }}
                selected={isViewing || isSelected}
                sx={{ cursor: "pointer" }}
              >
                {/* Checkbox */}
                <TableCell padding="checkbox" sx={{ width: "48px" }}>
                  <Checkbox
                    color="primary"
                    checked={isSelected}
                    onChange={(e) => { e.stopPropagation(); handleSelect(item); }}
                    sx={{ "&.Mui-checked": { color: dt.blue } }}
                  />
                </TableCell>

                {/* Product */}
                <TableCell>
                  <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
                    <ImageBox size="small" src={item.file?.content} />
                    <Box sx={{ minWidth: 0 }}>
                      <Box
                        onClick={(e) => { e.stopPropagation(); handleEdit(item); }}
                        sx={{
                          color: dt.textLink,
                          fontWeight: 600,
                          fontSize: "13px",
                          fontFamily: dt.fontMono,
                          letterSpacing: "0.3px",
                          cursor: "pointer",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          "&:hover": { textDecoration: "underline", color: dt.blueHover },
                        }}
                      >
                        {item.product?.number}
                      </Box>
                      {item.alternativeNumber && (
                        <Box sx={{
                          color: dt.textMuted,
                          fontSize: "11px",
                          fontFamily: dt.fontMono,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}>
                          {item.alternativeNumber}
                        </Box>
                      )}
                    </Box>
                  </Box>
                </TableCell>

                {/* Brand / Category */}
                <TableCell sx={{ maxWidth: "160px" }}>
                  <Tooltip title={item.product?.manufacturer?.name || ""} enterDelay={400} arrow>
                    <Box sx={{
                      fontWeight: 500,
                      textTransform: "uppercase",
                      fontSize: "13px",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}>
                      {item.product?.manufacturer?.name}
                    </Box>
                  </Tooltip>
                  <Box sx={{
                    color: dt.textSecondary,
                    fontSize: "12px",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}>
                    {item.product?.category?.name}
                  </Box>
                </TableCell>

                {/* Group */}
                <TableCell sx={{ width: "130px" }}>
                  <GroupBadge group={group} tInventory={tInventory as (k: string) => string} />
                </TableCell>

                {/* Condition */}
                <TableCell sx={{ width: "120px" }}>
                  <Box sx={{ fontSize: "13px" }}>
                    {tCommon(`productCondition.${productCondition(item?.conditionCode)}`)}
                  </Box>
                </TableCell>

                {/* Availability */}
                <TableCell sx={{ width: "130px" }}>
                  <AvailabilityBadge code={item.availabilityCode} tInventory={tInventory as (k: string, opts?: any) => string} />
                </TableCell>

                {/* Price */}
                <TableCell sx={{ width: "120px" }}>
                  <Box sx={{ fontFamily: dt.fontMono, fontWeight: 700, fontSize: "13px", whiteSpace: "nowrap" }}>
                    {item.price?.value != null ? (
                      <NumericFormat value={item.price.value} displayType="text" thousandSeparator=" " suffix=" EUR" />
                    ) : (
                      <Box component="span" sx={{ color: dt.textMuted }}>-</Box>
                    )}
                  </Box>
                </TableCell>

                {/* Quantity */}
                <TableCell sx={{ width: "100px" }}>
                  <Box sx={{ fontFamily: dt.fontMono, fontSize: "13px", whiteSpace: "nowrap" }}>
                    {item.quantity?.value != null
                      ? `${item.quantity.value} ${tCommon(`measurementUnits.${unitCode(item.quantity?.unitCode)}`)}`
                      : "-"}
                  </Box>
                </TableCell>

                {/* Actions */}
                <TableCell sx={{ width: "60px" }}>
                  {!isSelected ? (
                    <TableActionButton stopPropagation={true}>
                      <MenuItem onClick={() => handleFindInMarketplace(item)}>
                        <SearchIcon />
                        {tInventory("findInMarketplace")}
                      </MenuItem>
                      <MenuItem onClick={() => handleEdit(item)}>
                        <EditOutlinedIcon />
                        {tCommon("buttons.edit")}
                      </MenuItem>
                      <MenuItem onClick={() => handleMoveItem([item])}>
                        <FolderSharedOutlined />
                        {tInventory("move")}
                      </MenuItem>
                      <PermissionGuard module={Modules.Inventory} level={PermissionLevel.Admin}>
                        <MenuItem
                          onClick={() =>
                            confirm({
                              title: tInventory("deleteProduct"),
                              text: tInventory("deleteProductConfirm", {
                                name: getName(item.product.manufacturer.name, item.product.number),
                              }),
                              confirmButton: tInventory("yesDelete"),
                              closeButton: tCommon("common.no"),
                              severity: "error",
                              onConfirm: () => handleDelete(item),
                            })
                          }
                        >
                          <DeleteOutlinedIcon />
                          {tCommon("buttons.delete")}
                        </MenuItem>
                      </PermissionGuard>
                    </TableActionButton>
                  ) : (
                    <TableActionButton stopPropagation={true}>
                      <Box sx={{ px: 2, py: 1, fontSize: "12px", color: dt.textSecondary, fontFamily: dt.font }}>
                        {tInventory("itemsSelected", {
                          count: selectedItems.length,
                          itemWord: selectedItems.length === 1 ? tInventory("item") : tInventory("items"),
                        })}
                      </Box>
                      <Divider />
                      <MenuItem onClick={() => handleMoveItem(selectedItems)}>
                        <FolderSharedOutlined />
                        {tInventory("move")}
                      </MenuItem>
                      <PermissionGuard module={Modules.Inventory} level={PermissionLevel.Admin}>
                        <MenuItem
                          onClick={() =>
                            confirm({
                              title: tInventory("deleteProduct"),
                              text: tInventory("deleteProductsConfirm", {
                                count: selectedItems.length,
                                productWord: selectedItems.length === 1 ? tInventory("product") : tInventory("products"),
                              }),
                              confirmButton: tInventory("yesDelete"),
                              closeButton: tCommon("common.no"),
                              severity: "error",
                              onConfirm: () => handleDeleteBulk(),
                            })
                          }
                        >
                          <DeleteOutlinedIcon />
                          {tCommon("buttons.delete")}
                        </MenuItem>
                      </PermissionGuard>
                    </TableActionButton>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
        </MuiTable>
      </TableContainer>

      {/* Pagination */}
      <Box sx={paginationWrapperSx}>
        <LightTablePagination
          totalCount={stockItems?.totalCount || 0}
          currentPage={query.pageNo}
          pageSize={query.pageSize}
          pageSizeOptions={[10, 20, 50, 100]}
          onPageChange={handlePageNoChange}
          onPageSizeChange={handlePageSizeChange}
          rowsPerPageLabel={tInventory("rowsPerPage")}
          ofLabel={tInventory("of")}
        />
      </Box>
    </Box>
  );

  // ─── Render mobile cards ───────────────────────────────────

  const renderMobileCards = () => (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "10px", overflow: "hidden", width: "100%" }}>
      {stockItems?.items.map((item, index) => (
        <MobileStockItemCard
          key={item.id}
          item={item}
          groups={groups}
          index={index}
          tInventory={tInventory as (key: string, options?: any) => string}
          tCommon={tCommon as (key: string, options?: any) => string}
          onClick={handleStockItemSelect}
        />
      ))}

      {/* Pagination */}
      <Box sx={paginationWrapperSx}>
        <LightTablePagination
          totalCount={stockItems?.totalCount || 0}
          currentPage={query.pageNo}
          pageSize={query.pageSize}
          pageSizeOptions={[10, 20, 50]}
          onPageChange={handlePageNoChange}
          onPageSizeChange={handlePageSizeChange}
          rowsPerPageLabel={tInventory("rowsPerPage")}
          ofLabel={tInventory("of")}
        />
      </Box>
    </Box>
  );

  // ─── Render filter content ─────────────────────────────────

  const renderFilterContent = () => (
    <InventoryFilterBar
      query={query}
      groups={groups}
      manufacturers={manufacturers}
      categories={categories}
      tInventory={tInventory as (key: string, options?: any) => string}
      tCommon={tCommon as (key: string, options?: any) => string}
      onChange={handleQueryChange}
    />
  );

  // ─── Current group for MoveDialog ──────────────────────────

  const getGroupNameForItems = (): string => {
    if (itemsToMove.length === 0) return "";
    const firstItem = itemsToMove[0];
    const group = groups.find((g) => g.id === firstItem.groupId);
    if (!group) return "";
    return group.isPrivate ? tInventory("internalStockLabel") : tInventory("forSaleLabel");
  };

  const getMoveDialogGroups = (): StockItemGroupStats[] => {
    if (itemsToMove.length === 0) return groups;
    const firstItem = itemsToMove[0];
    return groups.filter((g) => g.id !== firstItem.groupId);
  };

  // ═══════════════════════════════════════════════════════════
  //  RENDER
  // ═══════════════════════════════════════════════════════════

  return (
    <Box
      sx={{
        fontFamily: dt.font,
        "& *:not(.MuiSvgIcon-root)": { fontFamily: `${dt.font}` },
        backgroundColor: dt.pageBg,
        backgroundImage: `radial-gradient(circle at 1px 1px, ${dt.pageDotColor} 1px, transparent 0)`,
        backgroundSize: "28px 28px",
        overflowX: "hidden",
        flex: 1,
        pb: 4,
      }}
    >
      {/* Notifications */}
      <ActionToast
        open={!!success}
        variant="success"
        message={success || ""}
        onClose={() => dispatch(setSuccess(undefined))}
        duration={6000}
      />
      <ActionToast
        open={!!error}
        variant="error"
        message={NotificationMessage.error(error).message || ""}
        onClose={() => dispatch(setError(undefined))}
        duration={6000}
      />

      {/* ─── Page Header ────────────────────────────────── */}
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: { xs: 1.5, md: 2 },
          px: { xs: 2, md: 3 },
          pt: { xs: 2, md: 3 },
          pb: 2,
          animation: "headerReveal 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Box
            sx={{
              fontSize: { xs: "20px", sm: "22px", md: "28px" },
              fontWeight: 800,
              color: dt.textPrimary,
              letterSpacing: "-0.5px",
            }}
          >
            {tInventory("myWarehouse")}
          </Box>
          <Box
            sx={{
              width: "48px",
              height: "3px",
              borderRadius: "2px",
              background: `linear-gradient(90deg, ${dt.blue}, ${dt.blue}00)`,
              mt: 1,
            }}
          />
        </Box>

        {/* Manage Products Button */}
        <Button
          onClick={handleManageOpen}
          endIcon={<ArrowDownIcon />}
          sx={{
            background: accentGradientDark,
            borderRadius: dt.radiusPill,
            padding: isPhone ? "7px 16px" : "11px 24px",
            textTransform: "none",
            fontWeight: 600,
            fontSize: isPhone ? "12px" : "14px",
            fontFamily: dt.font,
            color: "#FFFFFF",
            boxShadow: "0 2px 8px rgba(52,152,219,0.25)",
            whiteSpace: "nowrap",
            flexShrink: 0,
            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            "&:hover": {
              background: accentGradientDarkHover,
              transform: "translateY(-2px)",
              boxShadow: "0 6px 20px rgba(52,152,219,0.3)",
            },
          }}
        >
          {isPhone ? tInventory("manageProducts") : tInventory("manageProducts")}
        </Button>

        {/* Manage Products Dropdown Menu */}
        <Menu
          anchorEl={manageAnchor}
          open={Boolean(manageAnchor)}
          onClose={handleManageClose}
          slotProps={{
            paper: {
              sx: {
                borderRadius: "12px",
                boxShadow: "0 4px 20px rgba(0,0,0,0.08), 0 1px 4px rgba(0,0,0,0.04)",
                border: `1px solid ${dt.cardBorder}`,
                mt: 0.5,
              },
            },
          }}
        >
          {forSaleGroup && (
            <MenuItem
              onClick={() => { handleManageClose(); navigate(`/supplier/new/${forSaleGroup.id}`); }}
              sx={{ fontFamily: dt.font, fontSize: "13px", gap: "8px" }}
            >
              <AddIcon sx={{ fontSize: "18px", color: dt.blue }} />
              {tInventory("addToForSale")}
            </MenuItem>
          )}
          {internalGroup && (
            <MenuItem
              onClick={() => { handleManageClose(); navigate(`/supplier/new/${internalGroup.id}`); }}
              sx={{ fontFamily: dt.font, fontSize: "13px", gap: "8px" }}
            >
              <AddIcon sx={{ fontSize: "18px", color: dt.textSecondary }} />
              {tInventory("addToInternal")}
            </MenuItem>
          )}
          <Divider />
          {forSaleGroup && (
            <MenuItem
              onClick={() => { handleManageClose(); navigate(`/supplier/upload/${forSaleGroup.id}`); }}
              sx={{ fontFamily: dt.font, fontSize: "13px", gap: "8px" }}
            >
              {tInventory("uploadToForSale")}
            </MenuItem>
          )}
          {internalGroup && (
            <MenuItem
              onClick={() => { handleManageClose(); navigate(`/supplier/upload/${internalGroup.id}`); }}
              sx={{ fontFamily: dt.font, fontSize: "13px", gap: "8px" }}
            >
              {tInventory("uploadToInternal")}
            </MenuItem>
          )}
        </Menu>
      </Box>

      {/* ─── Content Area ───────────────────────────────── */}
      <Box
        sx={{
          px: { xs: 2, md: 3 },
          animation: "fadeUp 0.5s cubic-bezier(0.4, 0, 0.2, 1) 0.15s both",
        }}
      >
        {/* Filters: inline on desktop, accordion on tablet/phone */}
        {isCompact ? (
          <Accordion
            expanded={filtersExpanded}
            onChange={() => setFiltersExpanded(!filtersExpanded)}
            sx={{
              backgroundColor: dt.cardBg,
              borderRadius: `${dt.radiusSm} !important`,
              border: `1px solid ${dt.cardBorder}`,
              boxShadow: "none !important",
              "&:before": { display: "none" },
              "&.Mui-expanded": { margin: "0 !important" },
              mb: 2,
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                minHeight: "44px !important",
                "& .MuiAccordionSummary-content": { margin: "8px 0 !important" },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <FilterListIcon sx={{ fontSize: "18px", color: dt.textSecondary }} />
                <Box sx={{ fontSize: "13px", fontWeight: 600, color: dt.textSecondary, fontFamily: dt.font }}>
                  {tInventory("filters")}
                </Box>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0, pb: 2, px: 2 }}>
              {renderFilterContent()}
            </AccordionDetails>
          </Accordion>
        ) : (
          <Box sx={{
            backgroundColor: dt.cardBg,
            borderRadius: dt.radius,
            border: `1px solid ${dt.cardBorder}`,
            boxShadow: dt.cardShadow,
            padding: "16px 20px",
            mb: 2,
          }}>
            {renderFilterContent()}
          </Box>
        )}

        {/* Loading */}
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <Loader />
          </Box>
        ) : !stockItems?.items.length ? (
          /* Empty state */
          <Box sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            py: 8,
            backgroundColor: dt.cardBg,
            borderRadius: dt.radius,
            border: `1px solid ${dt.cardBorder}`,
            boxShadow: dt.cardShadow,
          }}>
            <TableEmpty
              iconComponent={BoxSearchIcon}
              text={isQueryEmpty ? tInventory("noItemsYet") : tInventory("noItemsFound")}
            >
              {isQueryEmpty ? (
                <Button
                  onClick={handleManageOpen}
                  sx={{
                    background: accentGradientDark,
                    borderRadius: dt.radiusPill,
                    padding: "10px 24px",
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: "13px",
                    fontFamily: dt.font,
                    color: "#FFFFFF",
                    mt: 2,
                    "&:hover": {
                      background: accentGradientDarkHover,
                    },
                  }}
                >
                  {tInventory("manageProducts")}
                </Button>
              ) : (
                <Box sx={{ color: dt.textMuted, fontSize: "13px", mt: 1, fontFamily: dt.font }}>
                  {tInventory("adjustFilters")}
                </Box>
              )}
            </TableEmpty>
          </Box>
        ) : (
          /* Table or Cards */
          isPhone ? renderMobileCards() : renderTable()
        )}
      </Box>

      {/* ─── Detail Drawer ──────────────────────────────── */}
      {stockItem && (
        <ClickAwayListener onClickAway={handleClickAway}>
          <div className="absolute">
            <Drawer
              variant="persistent"
              anchor="right"
              open={productDetails}
              onClose={() => setProductDetails(false)}
              hideBackdrop
              elevation={0}
              PaperProps={{
                sx: {
                  background: "#FFF !important",
                  borderLeft: `1px solid ${dt.cardBorder}`,
                  marginTop: "150px",
                  height: "calc(100% - 150px)",
                },
              }}
            >
              <StockItemDetails />
            </Drawer>
          </div>
        </ClickAwayListener>
      )}

      {/* ─── Move Dialog ────────────────────────────────── */}
      {groups.length > 0 && (
        <MoveStockItemDialog
          key={itemsToMove.length}
          open={isMoveDialogOpen}
          onClose={() => setIsMoveDialogOpen(false)}
          onConfirm={(value) => handleChangeStockItemsGroup(value)}
          stockItems={itemsToMove}
          groups={getMoveDialogGroups()}
          groupName={getGroupNameForItems()}
        />
      )}
    </Box>
  );
};

export default InventoryTable;
